// utils/attachmentUtils.ts
import { useState, useEffect } from 'react';
import { StorageService } from '../lib/storage';

export interface AttachmentWithSignedUrl {
  path: string;
  type: string;
  size: number;
  url: string;
  name: string;
  signedUrl?: string;
}

/**
 * Process attachments to add signed URLs for private storage access
 */
export async function processAttachments(
  attachments: any[],
  contextType: 'conversation' | 'community' | 'profile',
  expiresIn: number = 3600
): Promise<AttachmentWithSignedUrl[]> {
  if (!attachments || !Array.isArray(attachments) || attachments.length === 0) {
    return [];
  }

  try {
    // Filter out attachments that don't have proper path structure
    const validAttachments = attachments.filter(att => {
      if (!att.path || typeof att.path !== 'string') {
        console.warn('Invalid attachment path:', att);
        return false;
      }
      return true;
    }).map(att => {
      // Handle legacy public URLs - convert them to private URLs for signed URL generation
      if (att.url && att.url.includes('/storage/v1/object/public/')) {
        // This is a legacy public URL, replace with private placeholder
        return {
          ...att,
          url: `private://${contextType === 'community' ? 'community-attachments' : 'message_attachments'}/${att.path}`
        };
      }
      return att;
    });

    if (validAttachments.length === 0) {
      return attachments.map(att => ({
        ...att,
        signedUrl: att.url || '' // Fallback to original URL
      }));
    }

    return await StorageService.addSignedUrls(validAttachments, contextType, expiresIn);
  } catch (error) {
    console.error('Error processing attachments:', error);
    // Return original attachments if signing fails
    return attachments.map(att => ({
      ...att,
      signedUrl: att.url || '' // Fallback to original URL
    }));
  }
}

/**
 * Hook to automatically process attachments with signed URLs
 */
export function useAttachments(
  attachments: any[] | null | undefined,
  contextType: 'conversation' | 'community' | 'profile'
) {
  const [processedAttachments, setProcessedAttachments] = useState<AttachmentWithSignedUrl[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const processAndSetAttachments = async () => {
      if (!attachments || attachments.length === 0) {
        setProcessedAttachments([]);
        return;
      }

      console.log('Processing attachments:', attachments);
      setIsLoading(true);
      try {
        const processed = await processAttachments(attachments, contextType);
        console.log('Processed attachments with signed URLs:', processed);
        setProcessedAttachments(processed);
      } catch (error) {
        console.error('Error processing attachments:', error);
        setProcessedAttachments(attachments || []);
      } finally {
        setIsLoading(false);
      }
    };

    processAndSetAttachments();
  }, [attachments, contextType]);

  return { processedAttachments, isLoading };
}

/**
 * Get the appropriate context type based on the current context
 */
export function getContextType(pathname: string): 'conversation' | 'community' | 'profile' {
  if (pathname.includes('/messages')) {
    return 'conversation';
  } else if (pathname.includes('/communities')) {
    return 'community';
  } else if (pathname.includes('/profile') || pathname.includes('/users')) {
    return 'profile';
  }
  
  // Default fallback
  return 'community';
}

/**
 * Get attachment type information
 */
export function getAttachmentType(attachment: AttachmentWithSignedUrl) {
  const isImage = attachment.type.startsWith('image/');
  const isPdf = attachment.type === 'application/pdf';
  const isText = attachment.type.includes('text') || attachment.type.includes('document');
  
  return { isImage, isPdf, isText };
}

/**
 * Validate file structure and migrate if necessary
 */
export function validateFileStructure(filePath: string, userId: string, contextId: string): boolean {
  // Expected structure: user_id/context_id/filename
  const parts = filePath.split('/');
  
  if (parts.length !== 3) {
    console.warn(`Invalid file structure: ${filePath}. Expected: user_id/context_id/filename`);
    return false;
  }

  const [fileUserId, fileContextId] = parts;
  
  if (fileUserId !== userId) {
    console.warn(`File user ID mismatch: ${fileUserId} !== ${userId}`);
    return false;
  }

  if (fileContextId !== contextId) {
    console.warn(`File context ID mismatch: ${fileContextId} !== ${contextId}`);
    return false;
  }

  return true;
}

/**
 * Clean file name for storage
 */
export function cleanFileName(fileName: string): string {
  // Remove any path separators and normalize
  return fileName
    .replace(/[\/\\]/g, '_')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .substring(0, 255); // Limit length
}